import { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { Button, Spinner } from '../ui';
import { getAIPricingSuggestion } from '../../lib/api';

/**
 * AIPricingModal
 *
 * Displays AI-generated pricing suggestions for raffle items.
 * Shows estimated value, recommended ticket price, quantity, and reasoning.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Close handler
 * @param {function} onApply - Called with suggestion when user applies it
 * @param {object} itemDetails - { photos, title, description, category, condition }
 */
export default function AIPricingModal({
  isOpen,
  onClose,
  onApply,
  itemDetails,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  // Fetch suggestion when modal opens
  useEffect(() => {
    if (isOpen && !suggestion && !loading) {
      fetchSuggestion();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSuggestion(null);
      setError(null);
    }
  }, [isOpen]);

  async function fetchSuggestion() {
    setLoading(true);
    setError(null);

    try {
      const result = await getAIPricingSuggestion({
        photos: itemDetails.photos || [],
        title: itemDetails.title || '',
        description: itemDetails.description || '',
        category: itemDetails.category || '',
        condition: itemDetails.condition || '',
      });

      if (result.success && result.suggestion) {
        setSuggestion(result.suggestion);
      } else {
        throw new Error('Invalid response from AI service');
      }
    } catch (err) {
      console.error('AI pricing error:', err);
      setError(err.message || 'Failed to get AI suggestion. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleApply() {
    if (suggestion) {
      onApply(suggestion);
      onClose();
    }
  }

  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="AI Pricing Suggestion"
      description="Based on your item details"
      size="md"
      dark
    >
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <Spinner size="lg" />
          <p className="text-cream/60 mt-4">Analyzing your item...</p>
          <p className="text-cream/40 text-sm mt-1">This may take a few seconds</p>
        </div>
      )}

      {error && (
        <div className="text-center py-8">
          <div className="w-14 h-14 bg-danger/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-cream mb-2">Unable to generate suggestion</p>
          <p className="text-cream/60 text-sm mb-6">{error}</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={fetchSuggestion}>
              Try Again
            </Button>
          </div>
        </div>
      )}

      {suggestion && !loading && !error && (
        <div className="space-y-6">
          {/* Estimated Value */}
          <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm font-medium text-cream/60">Estimated Value</span>
            </div>
            <p className="text-2xl font-bold text-cream">
              €{suggestion.estimatedValue.low} - €{suggestion.estimatedValue.high}
            </p>
          </div>

          {/* Recommended Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
              <span className="text-sm text-primary/80">Entry Price</span>
              <p className="text-2xl font-bold text-primary">€{formatPrice(suggestion.ticketPrice)}</p>
            </div>
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
              <span className="text-sm text-primary/80">Number of Entries</span>
              <p className="text-2xl font-bold text-primary">{suggestion.ticketQuantity}</p>
            </div>
          </div>

          {/* Potential Earnings */}
          <div className="flex justify-between items-center py-3 border-t border-midnight-lighter">
            <span className="text-cream/60">Total Potential</span>
            <span className="text-xl font-semibold text-cream">
              €{formatPrice(suggestion.ticketPrice * suggestion.ticketQuantity)}
            </span>
          </div>

          {/* AI Reasoning */}
          <div className="bg-midnight-light/50 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-lg">🤖</span>
              <p className="text-cream/70 text-sm">{suggestion.reasoning}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Keep My Prices
            </Button>
            <Button onClick={handleApply} className="flex-1">
              Apply Suggestion
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
